import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { RocketIcon, ChevronDown } from 'lucide-react';
import '@r/assets/styles/animation.css';
import { cn } from 'ressources/src/utils/utils';
import { useTranslation } from 'react-i18next';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';

interface ProgressProps extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  min?: number;
  max: number;
  isTargetPointLogic?: boolean;
  tooltipValue?: number;
  tooltipValueSuffix?: string;
}

const Progress = forwardRef<ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  (
    {
      className,
      value = 0,
      min = 0,
      max,
      isTargetPointLogic = false,
      tooltipValue,
      tooltipValueSuffix,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const slideAnimationDuration = 0.5;
    const formattedTooltipValue = tooltipValue ?? 0;
    const formattedTooltipSuffix =
      tooltipValueSuffix ??
      (isTargetPointLogic ? ` ${pointsTranslation(t, formattedTooltipValue, false)}` : undefined);
    const shouldDisplayTooltip = formattedTooltipValue > 1;
    const progressBar = (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn('overflow-hidden relative w-full h-3 rounded-full bg-secondary', className)}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="relative flex-1 w-full h-full rounded-full transition-all bg-primary"
          style={{
            transform: `translateX(-${100 - (value || 0)}%)`,
            animation: `full-width-basic ${slideAnimationDuration}s ease-out forwards`
          }}
        />
      </ProgressPrimitive.Root>
    );

    return (
      <div className="px-5 pt-[3.6rem] pb-[2.5rem] sm:px-16">
        <div className="relative">
          <div
            className="relative flex-1 w-full h-full transition-all"
            style={{
              transform: `translateX(-${100 - (value || 0)}%)`,
              animation: `full-width-basic ${slideAnimationDuration}s ease-out forwards`
            }}
          >
            <div className="absolute top-[-4rem] right-0 flex translate-x-[50%] flex-nowrap items-center whitespace-nowrap rounded-md bg-primary px-3 py-2 text-sm xl:flex">
              <div className="flex justify-center items-center w-6 rounded-full border border-white aspect-square">
                <RocketIcon size={13} color="white" />
              </div>
              <span className="hidden text-white sm:ml-2 sm:block">
                {t('admin.company.target.info')}
              </span>
              <div className="absolute bottom-[-1.2rem] left-0 hidden w-full justify-center xl:flex">
                <ChevronDown className="stroke-primary" size={20} />
              </div>
            </div>
          </div>

          <div
            className="absolute top-0 z-40 box-border flex aspect-square h-full translate-x-[-50%] items-center justify-center rounded-full bg-foreground"
            style={{ left: '0%' }}
          >
            <div className="w-1 h-1 rounded-full bg-background" />

            <div className="absolute bottom-[-2.3rem] text-foreground md:bottom-[-3.5rem] lg:bottom-[-2.3rem] xl:bottom-[-3.5rem] 2xl:bottom-[-2.3rem]">
              <span className="hidden whitespace-nowrap md:inline-block">
                <FormattedNumber
                  value={min || 0}
                  style={isTargetPointLogic ? 'decimal' : undefined}
                  suffix={
                    isTargetPointLogic ? ` ${pointsTranslation(t, min || 0, false)}` : undefined
                  }
                />
              </span>
            </div>
          </div>

          <div
            className="absolute top-0 z-40 box-border flex aspect-square h-full translate-x-[-50%] items-center justify-center rounded-full bg-foreground"
            style={{ left: '100%' }}
          >
            <div className="w-1 h-1 rounded-full bg-background" />

            <div className="absolute bottom-[-2.3rem] text-foreground md:bottom-[-3.5rem] lg:bottom-[-2.3rem] xl:bottom-[-3.5rem] 2xl:bottom-[-2.3rem]">
              <span className="hidden ml-2 whitespace-nowrap md:inline-block">
                <FormattedNumber
                  value={max}
                  style={isTargetPointLogic ? 'decimal' : undefined}
                  suffix={isTargetPointLogic ? ` ${pointsTranslation(t, max, false)}` : undefined}
                />
              </span>
            </div>
          </div>

          {shouldDisplayTooltip ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>{progressBar}</div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="flex gap-2 justify-center items-center whitespace-nowrap">
                    <span className="w-3 h-3 rounded-sm bg-primary" />
                    <span>
                      <span className="font-bold">
                        <FormattedNumber
                          value={formattedTooltipValue}
                          style="decimal"
                          suffix={formattedTooltipSuffix}
                        />
                      </span>{' '}
                      {t('logic.progress.tooltip.generated.company')}
                    </span>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            progressBar
          )}
        </div>
      </div>
    );
  }
);

Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
